/* =====================================================================
   EPOMAKER ARCADE — mini-game shell
   ---------------------------------------------------------------------
   Two layers, on purpose:

     mountArcade(root) — the shell. Owns sound, fullscreen, storage, key
                         routing, kiosk/embed modes, the game picker.
                         Knows nothing about any particular game.
     GAMES[]           — each game is a module with { id, name, mount(api) }.
                         Adding game #2 means writing one more module and
                         pushing it into GAMES. Nothing else changes.

   Everything is scoped to the root element, so the same section can appear
   more than once on a page without the two instances fighting.

   Settings come from data- attributes on the root (the section writes them
   from its schema); URL parameters override them, which is what lets one
   page serve a campaign retarget or a booth screen without a new template.

     data-target   8.8        seconds to stop at
     data-decimals 3|2        clock precision; a hit is the clock reading the target
     data-claim    1          voucher claim card on a hit; locks the target to the setting
     data-gated    1          claim on but nobody logged in: the game stays inert
     data-mode     normal|blind|pitch
     data-kiosk    1          booth screen: big type, no CTA, idle attract loop
     data-embed    1          inside an iframe: no logo/fullscreen, posts height
     data-fill     1          owns the viewport (standalone file), auto-focus
   ===================================================================== */
(() => {
"use strict";

const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

/* ---------------------------------------------------------------------
   Storage — namespaced, and never fatal if blocked (Safari private mode
   throws on setItem).
   ------------------------------------------------------------------ */
const Store = {
  ns: "epo_arcade_v2:",
  get(k, fallback){
    try{
      const v = localStorage.getItem(this.ns + k);
      return v === null ? fallback : JSON.parse(v);
    }catch{ return fallback; }
  },
  set(k, v){
    try{ localStorage.setItem(this.ns + k, JSON.stringify(v)); }catch{}
  }
};

/* ---------------------------------------------------------------------
   Sound — WebAudio only, so there are no files to ship and it works
   offline at a booth. The context is created lazily on the first gesture
   because browsers refuse to start one before that.
   ------------------------------------------------------------------ */
const Sound = {
  ctx: null,
  on: Store.get("sound", true),
  ready(){
    if(this.ctx) return this.ctx;
    const AC = window.AudioContext || window.webkitAudioContext;
    if(!AC) return null;
    try{ this.ctx = new AC(); }catch{ return null; }
    return this.ctx;
  },
  resume(){
    const c = this.ready();
    if(c && c.state === "suspended") c.resume().catch(() => {});
  },
  tone(freq, dur, type = "square", gain = .05, delay = 0){
    if(!this.on) return;
    const c = this.ready();
    if(!c) return;
    const t0  = c.currentTime + delay;
    const osc = c.createOscillator();
    const g   = c.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, t0);
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(gain, t0 + .006);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    osc.connect(g).connect(c.destination);
    osc.start(t0);
    osc.stop(t0 + dur + .02);
  },
  /** a mechanical-keyboard-ish click, since that is rather the point */
  click(pitch = 1){
    this.tone(1600 * pitch, .035, "square", .035);
    this.tone(420  * pitch, .055, "triangle", .045);
  },
  chime(tier){
    const seq = {
      perfect: [[523.25,0],[659.25,.09],[783.99,.18],[1046.5,.27],[1318.5,.40]],
      great:   [[523.25,0],[783.99,.10],[1046.5,.20]],
      ok:      [[523.25,0],[659.25,.10]],
      miss:    [[196,0]],
      "void":  [[300,0],[220,.10]]
    }[tier] || [[440,0]];
    seq.forEach(([f, d]) => this.tone(f, .34, "sine", .075, d));
  },
  toggle(){
    this.on = !this.on;
    Store.set("sound", this.on);
    if(this.on){ this.resume(); this.click(); }
    return this.on;
  }
};

/* =====================================================================
   GAME MODULE — stop-8800
   ===================================================================== */
const StopClock = {
  id: "stop-8800",
  name: "Perfect Stop",
  blurb: "Start the clock, then stop it as close to the target as you can. No visual aid in Blind mode.",

  mount(api){
    const { q, opts, root } = api;

    const TARGET = opts.target;

    const MODES = [
      { id:"normal", label:"Normal",      next:"Blind mode",  hideAfter:Infinity, note:"" },
      { id:"blind",  label:"Blind",       next:"Pitch black", hideAfter:2.0,
        note:"Hidden from 2.0s — trust your timing" },
      { id:"pitch",  label:"Pitch black", next:"Normal",      hideAfter:0,
        note:"No clock at all. No mercy." }
    ];

    /* A stop this soon after the start is a key bounce or a double-tap,
       not an attempt. The prototype scored it as a ~0.05s run. */
    const BOUNCE_MS = 220;

    const TIERS = [
      { max:0.0010,  key:"perfect", grade:"FLAWLESS", msg:"Dead on. That was not luck." },
      { max:0.0050,  key:"great",   grade:"SSS",     msg:"Unreal timing." },
      { max:0.0200,  key:"great",   grade:"SS",      msg:"Razor sharp." },
      { max:0.0500,  key:"ok",      grade:"S",       msg:"So close." },
      { max:0.1000,  key:"ok",      grade:"A",       msg:"Nice control." },
      { max:0.2000,  key:"ok",      grade:"B",       msg:"Getting there." },
      { max:0.4000,  key:"miss",    grade:"C",       msg:"Feel the rhythm." },
      { max:Infinity,key:"miss",    grade:"MISS",    msg:"One more shot." }
    ];

    const el = {
      stage:q("stage"), clock:q("clock"), veil:q("veil"), veilMask:q("veilMask"),
      prompt:q("prompt"), result:q("result"), grade:q("grade"), msg:q("msg"),
      meter:q("meter"), pin:q("pin"), modeLabel:q("modeLabel"), runLabel:q("runLabel"),
      targetLabel:q("targetLabel"), bestVal:q("bestVal"), lastVal:q("lastVal"),
      runsVal:q("runsVal"), runsStrip:q("runsStrip"), modeBtnLabel:q("modeBtnLabel"),
      hudTitle:q("hudTitle"), claim:q("claim"), claimScore:q("claimScore"), claimWhen:q("claimWhen")
    };

    let state = "idle";     // idle | running | done
    let startTs = 0;
    let rafId = null;
    let modeIx = 0;
    let runs = [];          // this session only
    let missStreak = 0;
    let idleTimer = null;

    /* voided runs stay in the strip but do not count as attempts */
    const scored = () => runs.filter(r => r.tier !== "void");

    const bestKey = () => `best:${StopClock.id}:${TARGET.toFixed(3)}`;
    let best = Store.get(bestKey(), null);

    /* ?mode= wins, then the visitor's last pick, then the section default */
    const wanted = opts.modeUrl || Store.get("mode", "") || opts.mode || "normal";
    const wix = MODES.findIndex(m => m.id === wanted);
    if(wix >= 0) modeIx = wix;

    const mode = () => MODES[modeIx];

    /* a phone has no space bar; say what actually works there */
    const TAP = window.matchMedia("(hover:none) and (pointer:coarse)").matches;
    const GO       = TAP ? "Tap to start"      : "Press <b>Space</b> to start";
    const GO_HINT  = TAP ? ""                  : "<small>or any key · R B S F are shortcuts</small>";
    const STOP_MSG = TAP ? "Tap again to stop" : "Press again to stop";
    const AGAIN    = TAP ? "Tap to go again"   : "Press <b>Space</b> to go again";
    /* the prototype just blanked the screen and said "trust your timing",
       which reads as a freeze. Say plainly that the clock is still counting. */
    const RUNNING  = "<b>Clock still running</b> · " + (TAP ? "tap again to stop" : "press again to stop");

    const DEC = opts.decimals;
    const fmt = (v) => v.toFixed(DEC);
    /* real minus sign, not the hyphen toFixed hands back */
    const signed = (v) => (v >= 0 ? "+" : "−") + Math.abs(v).toFixed(DEC) + "s";

    /* What the player is told is "stop on the target", and what they see is the
       clock. So a hit is the clock reading exactly the target, nothing finer —
       FLAWLESS is reserved for that, and a run the tiers would call FLAWLESS but
       which reads one digit off drops to the next grade. Only with a claim on. */
    /* A tolerance widens that, still judged on the reading rather than the raw
       time, so 11.13 at ±0.02 is always a hit and 11.14 never is — the rule
       ops check a screenshot against is the number on it. */
    const TOL = opts.tolerance;

    const isHit = (score) => TOL > 0
      ? Math.abs(parseFloat(fmt(score)) - TARGET) <= TOL + 1e-9
      : fmt(score) === fmt(TARGET);

    /* Shown on the claim card, in the campaign's own time zone */
    const STAMP = new Intl.DateTimeFormat("en-US", {
      timeZone: "Asia/Shanghai", year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false
    });
    function showClaim(score){
      if(!el.claim) return;
      if(el.claimScore) el.claimScore.textContent = fmt(score) + "s · " + mode().label;
      if(el.claimWhen)  el.claimWhen.textContent  = STAMP.format(new Date()) + " (GMT+8)";
      el.claim.hidden = false;
      el.meter.hidden = true;
      el.stage.classList.add("has-claim");
    }

    /* Rebuilding innerHTML 60 times a second repaints every glyph — and each
       glyph carries a 60px glow. Slots are built once per length change, then
       only the characters that actually differ are touched, which on most frames
       is just the last one or two. */
    let slots = [];
    function paintClock(v){
      const s = fmt(v);
      if(slots.length !== s.length){
        el.clock.textContent = "";
        slots = [];
        const dot = s.indexOf(".");
        for(let i = 0; i < s.length; i++){
          const n = document.createElement("i");
          n.className = (s[i] === "." ? "arc-pt" : "arc-dg") + (dot >= 0 && i >= dot ? " arc-frac" : "");
          n.textContent = s[i];
          el.clock.appendChild(n);
          slots.push(s[i]);
        }
        return;
      }
      const nodes = el.clock.children;
      for(let i = 0; i < s.length; i++){
        if(slots[i] !== s[i]){ nodes[i].textContent = s[i]; slots[i] = s[i]; }
      }
    }
    const tierFor = (absDiff) => TIERS.find(t => absDiff <= t.max);
    /* the HIT message names the window, worked out from the setting, so the
       copy can never disagree with the rule */
    const HIT_TIER = { key:"perfect", grade:"HIT",
      msg: TOL > 0 ? `Inside the ${fmt(TARGET - TOL)}–${fmt(TARGET + TOL)} window.` : "Inside the window." };

    function renderChrome(){
      el.targetLabel.textContent  = fmt(TARGET);
      el.modeLabel.textContent    = mode().label;
      el.modeBtnLabel.textContent = mode().next;
      el.runLabel.textContent     = "Run " + (scored().length + 1);
      el.bestVal.textContent      = best === null ? "—" : fmt(best.score) + "s";
      const last = runs[runs.length - 1];
      el.lastVal.textContent      = !last ? "—" : last.tier === "void" ? "void" : fmt(last.score) + "s";
      el.runsVal.textContent      = String(scored().length);
      if(el.hudTitle) el.hudTitle.textContent = StopClock.name;
      /* only the standalone owns the document title; inside a store page the
         page's own <title> must survive */
      if(opts.fill) document.title = `EPOMAKER Arcade — ${StopClock.name}`;

      el.runsStrip.innerHTML = "";
      runs.slice(-12).forEach(r => {
        const d = document.createElement("span");
        d.className = "arc-runs__d t-" + r.tier;
        d.title = r.tier === "void" ? "Voided" : fmt(r.score) + "s";
        el.runsStrip.appendChild(d);
      });
    }

    const REDUCED = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let veiled = false;
    let scrambleTimer = null;

    /* Each dot flickers on a jittered 55-115ms interval. Fast and irregular on
       purpose: the player must be able to see that something is alive, but must
       not be able to count it. Anything with a steady period (a 1s pulse, a
       looping sweep) is a metronome and would hand them the answer. */
    function startScramble(){
      stopScramble();
      if(REDUCED) return;
      const dots = el.veilMask.getElementsByTagName("i");
      const step = () => {
        for(let i = 0; i < dots.length; i++){
          dots[i].style.opacity = (0.14 + Math.random() * 0.46).toFixed(2);
        }
        scrambleTimer = setTimeout(step, 55 + Math.random() * 60);
      };
      step();
    }
    function stopScramble(){
      clearTimeout(scrambleTimer);
      scrambleTimer = null;
    }

    function setVeil(on){
      if(on === veiled) return;              // tick() calls this every frame
      veiled = on;
      el.clock.classList.toggle("is-veiled", on);
      el.veil.classList.toggle("is-on", on);
      el.stage.classList.toggle("is-veiled", on);
      if(on){
        startScramble();
        el.prompt.innerHTML = RUNNING;
      }else{
        stopScramble();
      }
    }

    function clearResult(){
      el.result.classList.remove("is-on");
      el.stage.classList.remove("has-result");
      el.grade.textContent = "";
      el.msg.textContent   = "";
      el.meter.hidden      = true;
      if(el.claim) el.claim.hidden = true;
      el.stage.classList.remove("has-claim");
    }

    function tick(){
      if(state !== "running") return;
      const t = (performance.now() - startTs) / 1000;
      if(t >= mode().hideAfter){
        setVeil(true);
      }else{
        setVeil(false);
        paintClock(t);
      }
      rafId = requestAnimationFrame(tick);
    }

    /* ready -> a deliberate, visible armed state. The prototype restarted the
       clock 60ms after the "play again" press, so players were already losing
       time before they knew a run had begun. */
    function toReady(silent){
      state = "idle";
      cancelAnimationFrame(rafId);
      setVeil(false);
      el.stage.classList.remove("is-running");
      el.stage.classList.add("is-idle");
      paintClock(0);
      el.prompt.innerHTML = GO +
        (missStreak >= 3
          ? "<small>Tip: count it out in your head — one thousand, two thousand…</small>"
          : mode().note ? "<small>" + mode().note + "</small>" : GO_HINT);
      if(!silent) clearResult();
      renderChrome();
      armIdle();
    }

    function start(ts){
      state   = "running";
      startTs = ts;
      el.stage.classList.remove("is-idle");
      el.stage.classList.add("is-running");
      clearResult();
      paintClock(0);
      el.prompt.textContent = STOP_MSG;
      setVeil(mode().hideAfter === 0);   // pitch black: hidden from the first frame
      el.runLabel.textContent = "Run " + (scored().length + 1);
      Sound.click(1);
      clearTimeout(idleTimer);
      rafId = requestAnimationFrame(tick);
    }

    function stop(ts){
      if(state !== "running") return;
      cancelAnimationFrame(rafId);

      const elapsedMs = ts - startTs;

      /* key bounce / fat double-tap — not a real attempt */
      if(elapsedMs < BOUNCE_MS){
        state = "idle";
        setVeil(false);
        el.stage.classList.remove("is-running");
        el.stage.classList.add("is-idle");
        paintClock(0);
        el.prompt.innerHTML = GO;
        el.stage.classList.remove("has-result");
        api.toast("Double tap ignored");
        Sound.tone(180, .12, "sawtooth", .04);
        armIdle();
        return;
      }

      const score = elapsedMs / 1000;
      const diff  = score - TARGET;
      /* the claim rule only when a claim is on; the evergreen page keeps its tiers */
      const hit   = opts.claim && isHit(score);
      let tier    = hit ? TIERS[0] : tierFor(Math.abs(diff));
      if(opts.claim && !hit && tier === TIERS[0]) tier = TIERS[1];
      /* inside the tolerance but off the exact reading: a hit, not a FLAWLESS */
      if(hit && fmt(score) !== fmt(TARGET)) tier = HIT_TIER;

      state = "done";
      el.stage.classList.remove("is-running", "is-idle");

      setVeil(false);              // always reveal the true time, whatever the difficulty
      paintClock(score);

      runs.push({ score, diff, tier: tier.key });
      if(best === null || Math.abs(diff) < Math.abs(best.diff)){
        best = { score, diff };
        Store.set(bestKey(), best);
        if(runs.length > 1) api.toast("New personal best");
      }
      missStreak = tier.key === "miss" ? missStreak + 1 : 0;

      el.grade.className   = "arc-grade t-" + tier.key;
      el.grade.textContent = tier.grade;
      el.msg.innerHTML =
        `${tier.msg} &nbsp;·&nbsp; <b>${fmt(score)}s</b> &nbsp;·&nbsp; <b>${signed(diff)}</b>`;

      el.meter.hidden  = false;
      el.pin.className = "arc-meter__pin t-" + tier.key;
      el.pin.style.left = clamp(50 + (diff / 0.5) * 50, 1, 99) + "%";

      if(el.claim) el.claim.hidden = true;
      el.stage.classList.remove("has-claim");
      if(hit) showClaim(score);

      el.result.classList.add("is-on");
      el.stage.classList.add("has-result");
      el.prompt.innerHTML = AGAIN;

      el.stage.classList.remove("arc-flash", "arc-flash-gold");
      void el.stage.offsetWidth;
      el.stage.classList.add(tier.key === "perfect" ? "arc-flash-gold" : "arc-flash");

      Sound.click(.8);
      Sound.chime(tier.key);

      renderChrome();
      armIdle();
    }

    /** A run that loses focus is thrown away. This kills the obvious cheat
        (alt-tab to a stopwatch) and, more importantly, fixes a real bug:
        rAF halts on a hidden tab while the clock keeps running, so the
        prototype froze the display and then scored the frozen run. */
    function voidRun(reason){
      if(state !== "running") return;
      cancelAnimationFrame(rafId);
      state = "done";
      setVeil(false);
      el.stage.classList.remove("is-running");
      paintClock(0);
      runs.push({ score:0, diff:Infinity, tier:"void" });
      el.grade.className   = "arc-grade t-void";
      el.grade.textContent = "VOID";
      el.msg.textContent   = reason;
      el.meter.hidden      = true;
      if(el.claim) el.claim.hidden = true;
      el.stage.classList.remove("has-claim");
      el.result.classList.add("is-on");
      el.stage.classList.add("has-result");
      el.prompt.innerHTML  = AGAIN;
      Sound.chime("void");
      renderChrome();
      armIdle();
    }

    function resetSession(){
      runs = [];
      missStreak = 0;
      toReady();
      api.toast("Session reset");
    }

    function cycleMode(){
      modeIx = (modeIx + 1) % MODES.length;
      Store.set("mode", mode().id);
      best = Store.get(bestKey(), null);
      toReady();
      api.toast(mode().label + " mode");
    }

    /* kiosk attract loop: after a while idle, go back to a clean screen so the
       next person at the booth starts fresh */
    function armIdle(){
      clearTimeout(idleTimer);
      if(!opts.kiosk) return;
      idleTimer = setTimeout(() => {
        root.classList.add("is-attract");
        runs = [];
        missStreak = 0;
        toReady();
      }, 45000);
    }

    /* the single entry point for every input */
    function trigger(ts){
      if(opts.gated) return;
      root.classList.remove("is-attract");
      Sound.resume();
      if(state === "idle")         start(ts);
      else if(state === "running") stop(ts);
      else                         toReady();   // done -> armed, NOT auto-started
    }

    /* Event timestamps share the performance.now() time origin, so using them
       at both ends cancels most of the browser's input latency instead of
       baking it into the score twice. */
    function stamp(e){
      const now = performance.now();
      const t = e.timeStamp;
      return (typeof t === "number" && t > 0 && Math.abs(now - t) < 1000) ? t : now;
    }

    const IGNORE = new Set([
      "F5","F11","F12","Tab","Shift","Control","Alt","Meta","CapsLock",
      "ContextMenu","NumLock","ScrollLock","Insert","PrintScreen","Dead"
    ]);

    function onKey(e){
      if(e.repeat) return;
      if(!e.key || IGNORE.has(e.key)) return;
      if(e.ctrlKey || e.metaKey || e.altKey) return;   // let browser shortcuts through
      if(e.target.closest("button,a,input,select,textarea") && e.key !== " ") return;

      /* While the clock runs, EVERY key stops it — a shortcut must never
         swallow the stop. Shortcuts only apply when the clock is idle. */
      if(state !== "running"){
        const k = e.key.toLowerCase();
        if(k === "r"){ e.preventDefault(); resetSession();   return; }
        if(k === "b"){ e.preventDefault(); cycleMode();      return; }
        if(k === "s"){ e.preventDefault(); api.toggleSound();return; }
        if(k === "f"){ e.preventDefault(); api.toggleFull(); return; }
        /* Escape hands the keyboard back to the page */
        if(e.key === "Escape"){ root.blur(); return; }
      }

      e.preventDefault();
      trigger(stamp(e));
    }

    function onPointer(e){
      if(e.button > 0) return;                          // right / middle click
      if(e.target.closest("button,a,input")) return;
      /* the claim card is there to be read, selected and screenshot: a press
         on it must not throw it away by arming the next run */
      if(state === "done" && el.claim && !el.claim.hidden && el.claim.contains(e.target)) return;
      if(e.pointerType === "mouse"){
        root.focus({ preventScroll:true });
        e.preventDefault();
        trigger(stamp(e));
        return;
      }
      /* Touch and pen: a finger landing on the game may be starting a page
         scroll. Act on the lift, and only if it barely moved; the run is still
         timed from the touch-down, so the score keeps its precision. */
      const t0 = stamp(e), x0 = e.clientX, y0 = e.clientY, id = e.pointerId;
      const done = () => {
        el.stage.removeEventListener("pointerup", up);
        el.stage.removeEventListener("pointercancel", cancel);
      };
      const up = (u) => {
        if(u.pointerId !== id) return;
        done();
        if(Math.hypot(u.clientX - x0, u.clientY - y0) > 12) return;
        root.focus({ preventScroll:true });
        trigger(t0);
      };
      const cancel = (c) => { if(c.pointerId === id) done(); };
      el.stage.addEventListener("pointerup", up);
      el.stage.addEventListener("pointercancel", cancel);
    }

    /* Listeners live on the root, not on document: the browser's own focus
       model then decides whether a key belongs to the game or to the page.
       Space keeps scrolling the store page, and typing in the newsletter
       field never starts a run. */
    root.addEventListener("keydown", onKey);
    el.stage.addEventListener("pointerdown", onPointer);

    const onFocusOut = () => {
      /* setTimeout: focus moving between two children of the root reports a
         focusout before the focusin, and that is not really leaving */
      setTimeout(() => {
        if(!root.contains(document.activeElement)) voidRun("Focus left the game — run discarded");
      }, 0);
    };
    const onWinBlur = () => voidRun("Window lost focus — run discarded");
    const onVis = () => { if(document.hidden) voidRun("Tab hidden — run discarded"); };

    root.addEventListener("focusout", onFocusOut);
    window.addEventListener("blur", onWinBlur);
    document.addEventListener("visibilitychange", onVis);

    q("modeBtn").addEventListener("click", cycleMode);
    q("resetBtn").addEventListener("click", resetSession);

    toReady();

    return {
      destroy(){
        cancelAnimationFrame(rafId);
        clearTimeout(idleTimer);
        stopScramble();
        root.removeEventListener("keydown", onKey);
        root.removeEventListener("focusout", onFocusOut);
        el.stage.removeEventListener("pointerdown", onPointer);
        window.removeEventListener("blur", onWinBlur);
        document.removeEventListener("visibilitychange", onVis);
      }
    };
  }
};

const GAMES = [StopClock];
/* ---- game #2 goes here: push another module into GAMES, nothing else ---- */

/* =====================================================================
   Shell
   ===================================================================== */
function mountArcade(root){
  if(root.__arcade) return root.__arcade;

  const q = (name) => root.querySelector(`[data-arc="${name}"]`);
  const d = root.dataset;
  const url = new URLSearchParams(location.search);

  /* URL wins over the section setting, so one page can serve a campaign
     retarget or a booth screen without a new template */
  const num = (v) => { const n = parseFloat(v); return isFinite(n) ? n : null; };
  /* except with a voucher claim on: then ?target=1 would make a cheap "hit" */
  const claim = d.claim === "1";
  const target = (claim ? null : num(url.get("target"))) ?? num(d.target) ?? 8.8;
  const opts = {
    target: (target > 0.5 && target < 600) ? target : 8.8,
    decimals: d.decimals === "2" ? 2 : 3,
    tolerance: Math.min(Math.max((num(d.tolerance) ?? 0) / 1000, 0), 0.1),
    claim,
    gated:  d.gated === "1",
    mode:   d.mode || "",
    modeUrl: url.get("mode") || "",
    kiosk:  url.get("kiosk") === "1" || d.kiosk === "1",
    embed:  url.get("embed") === "1" || d.embed === "1",
    fill:   d.fill === "1"
  };

  root.classList.toggle("is-kiosk", opts.kiosk);
  root.classList.toggle("is-gated", opts.gated);
  root.classList.toggle("is-embed", opts.embed);

  /* Tell a host page how tall we want to be. */
  if(opts.embed && window.parent !== window){
    const postHeight = () => {
      try{
        window.parent.postMessage(
          { type:"epo-arcade:height", height: document.documentElement.scrollHeight },
          "*"
        );
      }catch{}
    };
    window.addEventListener("load", postHeight);
    window.addEventListener("resize", postHeight);
  }

  let toastTimer = null;
  function toast(text){
    const el = q("toast");
    if(!el) return;
    el.textContent = text;
    el.classList.add("is-on");
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => el.classList.remove("is-on"), 1800);
  }

  /* Fullscreen targets the game, not the document, so on a store page it
     goes edge-to-edge instead of blowing the whole theme up. */
  const fsTarget = opts.fill ? document.documentElement : root;
  async function toggleFull(){
    try{
      if(!document.fullscreenElement) await fsTarget.requestFullscreen();
      else await document.exitFullscreen();
    }catch{ toast("Fullscreen blocked by browser"); }
  }
  document.addEventListener("fullscreenchange", () => {
    const l = q("fsLabel");
    if(l) l.textContent = document.fullscreenElement ? "Exit full" : "Fullscreen";
    if(document.fullscreenElement === root) root.focus({ preventScroll:true });
  });

  const api = {
    root, q, opts, toast, toggleFull,
    store: Store, sound: Sound,
    toggleSound(){
      const on = Sound.toggle();
      const l = q("soundLabel");
      if(l) l.textContent = on ? "Sound On" : "Sound Off";
      q("soundBtn").setAttribute("aria-pressed", String(on));
      toast(on ? "Sound on" : "Sound off");
    }
  };

  const soundLabel = q("soundLabel");
  if(soundLabel) soundLabel.textContent = Sound.on ? "Sound On" : "Sound Off";
  q("soundBtn").setAttribute("aria-pressed", String(Sound.on));
  q("soundBtn").addEventListener("click", api.toggleSound);
  const fsBtn = q("fsBtn");
  if(fsBtn) fsBtn.addEventListener("click", toggleFull);

  /* The "click to play" gate exists only to explain why the keyboard is not
     yet listening. preventDefault on pointerdown stops the browser handing
     focus to anything else first, so root.focus() actually sticks. */
  const lock = q("lock");
  if(lock && !opts.gated) lock.addEventListener("pointerdown", (e) => {
    e.preventDefault();
    root.focus({ preventScroll:true });
  });

  /* engaged = the game currently holds the keyboard */
  root.addEventListener("focusin", () => root.classList.add("is-engaged"));
  root.addEventListener("focusout", () => setTimeout(() => {
    if(!root.contains(document.activeElement)) root.classList.remove("is-engaged");
  }, 0));

  let current = null;
  function play(id){
    if(current && current.destroy) current.destroy();
    const g = GAMES.find(x => x.id === id) || GAMES[0];
    const picker = q("picker");
    if(picker){ picker.classList.remove("is-on"); picker.setAttribute("aria-hidden", "true"); }
    current = g.mount(api);
  }

  /* Shown automatically once there is more than one game. With a single game
     it would just be a door in front of a door, so we skip it. */
  function showPicker(){
    const grid = q("pickerGrid"), picker = q("picker");
    if(!grid || !picker) return play(GAMES[0].id);
    grid.innerHTML = "";
    GAMES.forEach(g => {
      const c = document.createElement("button");
      c.className = "arc-card";
      c.innerHTML = `<h3>${g.name}</h3><p>${g.blurb}</p>`;
      c.addEventListener("click", () => play(g.id));
      grid.appendChild(c);
    });
    const soon = document.createElement("div");
    soon.className = "arc-card arc-card--soon";
    soon.innerHTML = "<h3>More coming</h3><p>New EPOMAKER mini-games drop with launches and campaigns.</p>";
    grid.appendChild(soon);
    picker.classList.add("is-on");
    picker.setAttribute("aria-hidden", "false");
  }

  const want = url.get("game");
  if(GAMES.length > 1 && !want) showPicker();
  else play(want || GAMES[0].id);

  /* Only the standalone/kiosk grabs focus on load. Inside a store page that
     would yank the viewport down to the game on every page view. */
  if(opts.fill || opts.kiosk) root.focus({ preventScroll:true });

  root.__arcade = { destroy(){ if(current && current.destroy) current.destroy(); root.__arcade = null; } };
  return root.__arcade;
}

function boot(scope){
  (scope || document).querySelectorAll(".epo-arcade").forEach(mountArcade);
}

if(document.readyState === "loading") document.addEventListener("DOMContentLoaded", () => boot());
else boot();

/* the theme editor re-renders sections in place */
document.addEventListener("shopify:section:load", (e) => boot(e.target));
document.addEventListener("shopify:section:unload", (e) => {
  e.target.querySelectorAll(".epo-arcade").forEach(r => r.__arcade && r.__arcade.destroy());
});

window.EPO = window.EPO || {};
window.EPO.Arcade = { mount: mountArcade, games: GAMES, boot };

})();
